import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Clock, Phone, MapPin, ExternalLink } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const f = c.form;

  const empty = { name: "", company: "", email: "", phone: "", interest: f.options[0], message: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = f.required;
    if (form.company.trim().length < 2) er.company = f.required;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = f.invalidEmail;
    if (form.message.trim().length < 10) er.message = f.tooShort;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success(f.success, { duration: 6000 });
      setForm(empty);
    } catch {
      toast.error(f.error, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const err = (k) =>
    errors[k] ? <p className="mt-1.5 text-xs text-red-400 font-mono" data-testid={`contact-error-${k}`}>{errors[k]}</p> : null;

  return (
    <section className="pt-40 pb-24 lg:pt-48 lg:pb-32" data-testid="contact-page">
      <div className="absolute inset-0 hud-lines opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-[11px] sm:text-xs font-bold tracking-[0.3em] uppercase text-sovereign flex items-center gap-3">
            <span className="w-8 h-px bg-sovereign/60" aria-hidden="true" />
            {c.eyebrow}
          </p>
        </Reveal>
        <h1 className="mt-6 font-display font-extrabold uppercase tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
          {c.title.map((l, i) => (
            <span key={i} className="block">{l}</span>
          ))}
        </h1>
        <Reveal delay={0.1}>
          <p className="mt-6 text-slate-400 max-w-2xl">{c.p}</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-[1.6fr_1fr] gap-10 items-start">
          <Reveal delay={0.15}>
            <form onSubmit={onSubmit} noValidate className="glass rounded-lg p-8 lg:p-10" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-2" htmlFor="cf-name">
                    {f.name} *
                  </label>
                  <input
                    id="cf-name"
                    className="field"
                    placeholder={f.namePh}
                    value={form.name}
                    onChange={set("name")}
                    data-testid="contact-input-name"
                  />
                  {err("name")}
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-2" htmlFor="cf-company">
                    {f.company} *
                  </label>
                  <input
                    id="cf-company"
                    className="field"
                    placeholder={f.companyPh}
                    value={form.company}
                    onChange={set("company")}
                    data-testid="contact-input-company"
                  />
                  {err("company")}
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-2" htmlFor="cf-email">
                    {f.email} *
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    className="field"
                    placeholder={f.emailPh}
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-input-email"
                  />
                  {err("email")}
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-2" htmlFor="cf-phone">
                    {f.phone}
                  </label>
                  <input
                    id="cf-phone"
                    className="field"
                    placeholder={f.phonePh}
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-2" htmlFor="cf-interest">
                  {f.interest} *
                </label>
                <select
                  id="cf-interest"
                  className="field"
                  value={form.interest}
                  onChange={set("interest")}
                  data-testid="contact-select-interest"
                >
                  {f.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-2" htmlFor="cf-message">
                  {f.message} *
                </label>
                <textarea
                  id="cf-message"
                  rows={6}
                  className="field resize-none"
                  placeholder={f.messagePh}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-textarea-message"
                />
                {err("message")}
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-sovereign text-obsidian font-semibold px-10 py-4 text-sm tracking-[0.12em] uppercase hover:shadow-[0_0_40px_rgba(0,240,255,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? f.sending : f.submit}
                {!sending && <span aria-hidden="true">→</span>}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.25}>
            <aside className="space-y-6">
              <div className="glass rounded-lg p-8" data-testid="contact-direct-panel">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-sovereign" aria-hidden="true" />
                  <h2 className="font-display font-bold text-slate-100">{c.side.directTitle}</h2>
                </div>
                <p className="mt-4 text-sm text-slate-400">{c.side.directText}</p>
                <a
                  href="mailto:office@emanteam.eu"
                  data-testid="contact-direct-email"
                  className="mt-3 inline-block text-lg font-semibold text-sovereign hover:text-slate-100 transition-colors duration-300"
                >
                  office@emanteam.eu
                </a>
                <p className="mt-6 pt-5 border-t border-sovereign/10 flex items-start gap-3 text-sm text-slate-400">
                  <Clock className="w-4 h-4 mt-0.5 text-sovereign shrink-0" aria-hidden="true" />
                  {c.side.response}
                </p>

                <div className="mt-6 pt-5 border-t border-sovereign/10" data-testid="contact-phones">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-sovereign" aria-hidden="true" />
                    <h3 className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{c.side.phoneLabel}</h3>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {c.side.phones.map((p, i) => (
                      <a
                        key={i}
                        href={`tel:${p.replace(/\s/g, "")}`}
                        data-testid={`contact-phone-${i}`}
                        className="block text-sm text-slate-200 hover:text-sovereign transition-colors duration-300"
                      >
                        {p}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-sovereign/10" data-testid="contact-address">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-sovereign" aria-hidden="true" />
                    <h3 className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{c.side.addressLabel}</h3>
                  </div>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{c.side.address}</p>
                </div>
              </div>

              <Reveal delay={0.3}>
                <div className="glass rounded-lg p-6" data-testid="contact-map-panel">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{c.side.mapLabel}</p>
                  <div className="mt-4 border border-sovereign/15 overflow-hidden" data-testid="contact-map-embed">
                    <iframe
                      title="EMAN TEAM Dynamics — Bd. Pipera, Voluntari"
                      src="https://www.openstreetmap.org/export/embed.html?bbox=26.1130%2C44.4845%2C26.1336%2C44.4989&layer=mapnik&marker=44.4916934%2C26.1233139"
                      className="w-full h-56 sm:h-64 block map-dark"
                      loading="lazy"
                    />
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${encodeURIComponent("Twin Towers, Bulevardul Pipera, 077190 Voluntari")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-map-open"
                    className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-sovereign transition-colors duration-300"
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                    {c.side.openMaps}
                  </a>
                </div>
              </Reveal>

              <div className="glass rounded-lg p-8" data-testid="contact-areas-panel">
                <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{c.side.areasTitle}</h2>
                <ul className="mt-5 space-y-3">
                  {c.side.areas.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-1.5 h-1.5 mt-1.5 bg-sovereign rotate-45 shrink-0" aria-hidden="true" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
